import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from "../utils/supabase"
import { getMerchantForOwner } from "../utils/merchantUtils"
import posthog, { isPostHogConfigured } from '../posthog'
import { logPostHogWorkflow } from '../posthogLogger'
import {
	Box,
	Button,
	Card,
	CardContent,
	InputAdornment,
	MenuItem,
	TextField,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
// import PasscodeScreen, { isPasscodeVerified } from '../components/PasscodeScreen'
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '../utils/currencyUtils'
import { getTimeZones } from '@vvo/tzdb'
import namiLogo from '../assets/nami.png'
import googleIcon from '../assets/store/google.svg'

// Get timezone list once
const TIMEZONES = getTimeZones()

// Figma design tokens
const BG = '#F4F5FA'
const PRIMARY = '#C85AFF'
const EMAIL_BG = '#F2E7FF'
const BORDER_GOOGLE = '#CFDBEC'

function MerchantSignup() {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// const [passcodeVerified, setPasscodeVerified] = useState(isPasscodeVerified())
	const [email, setEmail] = useState("")
	const [code, setCode] = useState("")
	const [step, setStep] = useState("auth") // auth | verify | merchant
	const [errorMessage, setErrorMessage] = useState("")
	const [session, setSession] = useState(null)
	const [creating, setCreating] = useState(false)
	const [checkingSession, setCheckingSession] = useState(true)
	const [setupCheckError, setSetupCheckError] = useState("")
	const [setupCheckAttempt, setSetupCheckAttempt] = useState(0)
	const [staleAnonSession, setStaleAnonSession] = useState(false)
	const otpRefs = useRef([])

	// merchant form state
	const defaultTimezone = useMemo(() => 'America/Los_Angeles', [])
	const [name, setName] = useState("")
	const [timezone, setTimezone] = useState(defaultTimezone)
	const [currency, setCurrency] = useState(DEFAULT_CURRENCY)
	const [phone, setPhone] = useState("")

	useEffect(() => {
		// IMPORTANT: never await Supabase calls inside this callback.
		// auth-js invokes it while holding its session lock (on tab visibility
		// change, token refresh, and page load with an existing session). Any query
		// issued from inside the callback needs that same lock, so it waits forever,
		// and every later query in the tab (e.g. "Create account") hangs until reload.
		// Only update state here; the effect below does the DB work afterwards.
		const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
			if (newSession?.user?.is_anonymous) {
				// Public pages (appointment details, agreement signing) create an
				// anonymous session for logged-out visitors. That is not a login:
				// treating it as one would skip the email step and show the
				// business form, creating a merchant owned by a throwaway user.
				// Show the email form and clear the anonymous session (see below).
				setSession(null)
				setCheckingSession(false)
				setStaleAnonSession(true)
				return
			}
			setSession(newSession)
			if (!newSession?.user?.id) setCheckingSession(false)
		})
		return () => listener.subscription.unsubscribe()
	}, [])

	// Clear a leftover anonymous session outside the auth callback (see the
	// lock warning above). Local scope only: no network round-trip is needed
	// to drop a throwaway session, and this must not fail on a bad connection.
	useEffect(() => {
		if (!staleAnonSession) return
		setStaleAnonSession(false)
		supabase.auth.signOut({ scope: 'local' }).catch((err) => {
			console.error('Failed to clear anonymous session:', err)
		})
	}, [staleAnonSession])

	// Runs once per signed-in user, outside the auth callback. Keyed on the user
	// id (not the session object) so token refreshes don't cancel an in-flight check.
	// If the lookup fails (flaky network, backend down) we show a retry screen.
	// We must NOT fall through to the sign-up form: a returning merchant would
	// create a second merchant row for the same owner_id.
	const userId = session?.user?.id
	useEffect(() => {
		if (!userId) return
		let cancelled = false
		const determineNextStep = async () => {
			setSetupCheckError("")
			setCheckingSession(true)
			try {
				const merchant = await getMerchantForOwner(userId)
				if (cancelled) return
				if (merchant?.id) {
					// Existing merchant: make sure the profile role is right, then go
					// to the dashboard. A failed upsert here is non-fatal for routing.
					await supabase.from('profiles').upsert(
						{ id: userId, role: 'merchant_owner' },
						{ onConflict: 'id' }
					)
					window.location.assign('/dashboard')
					return
				}
				// Confirmed: no merchant row for this owner. Safe to show sign-up.
				setStep('merchant')
				setCheckingSession(false)
			} catch (error) {
				console.error('Error checking merchant setup:', error)
				if (cancelled) return
				setSetupCheckError(
					"We couldn't load your account. Please check your connection and try again."
				)
				setCheckingSession(false)
			}
		}
		determineNextStep()
		return () => { cancelled = true }
	}, [userId, setupCheckAttempt])

	const signInWithGoogle = async () => {
		setErrorMessage("")
		try {
			await supabase.auth.signInWithOAuth({
				provider: 'google',
				options: { redirectTo: `${window.location.origin}/login` },
			})
		} catch (err) {
			setErrorMessage(err.message || "Google sign-in failed. Please try again.")
		}
	}

	const requestCode = async (e) => {
		e?.preventDefault?.()
		setErrorMessage("")
		try {
			const { error } = await supabase.auth.signInWithOtp({
				email: email.trim(),
				options: { shouldCreateUser: true },
			})
			if (error) throw error
			setStep("verify")
		} catch (err) {
			setErrorMessage(err.message || "Failed to send code. Please try again.")
		}
	}

	const verifyCode = async (e) => {
		e.preventDefault()
		setErrorMessage("")
		try {
			const { error } = await supabase.auth.verifyOtp({
				email: email.trim(),
				token: code.trim(),
				type: 'email',
			})
			if (error) throw error
		} catch (err) {
			setErrorMessage(err.message || "Invalid or expired code. Please try again.")
		}
	}

	const ensureProfile = async () => {
		if (!session?.user) return
		setErrorMessage("")
		const profileRow = {
			id: session.user.id,
			full_name: session.user.email || null,
			role: 'merchant_owner',
		}
		const { error } = await supabase.from('profiles').upsert(profileRow, { onConflict: 'id' })
		if (error) throw error
	}

	// "Not you?" on the business setup step. A signed-up merchant who logged in
	// with a different email (or Google account) lands here with no merchant
	// for that user and, without this, has no way back to the email form: the
	// session persists, so every visit to /login shows the setup form again.
	// Local scope so it always works, even on a bad connection.
	const useDifferentAccount = async () => {
		setErrorMessage("")
		try {
			await supabase.auth.signOut({ scope: 'local' })
		} catch (err) {
			console.error('Sign out failed:', err)
		}
		setSession(null)
		setStep('auth')
		setEmail('')
		setCode('')
		setCheckingSession(false)
	}

	const createMerchant = async (e) => {
		e.preventDefault()
		setErrorMessage("")
		setCreating(true)
		try {
			// Last line of defence against duplicate merchants: re-check right
			// before inserting. If the lookup fails we abort rather than guess.
			const existing = await getMerchantForOwner(session.user.id)
			if (existing?.id) {
				window.location.assign('/dashboard')
				return
			}

			await ensureProfile()
			const defaultWeeklyHours = {
				"0": [], "1": [{ start: "09:00", end: "17:00" }], "2": [{ start: "09:00", end: "17:00" }],
				"3": [{ start: "09:00", end: "17:00" }], "4": [{ start: "09:00", end: "17:00" }],
				"5": [{ start: "09:00", end: "17:00" }], "6": [],
			}
			const insert = {
				owner_id: session.user.id,
				name: name.trim(),
				timezone, currency,
				phone: phone.trim() || null,
				weekly_hours: defaultWeeklyHours,
				closed_recurring_dates: [],
				slug: 'temp-' + Date.now(),
				active: true,
			}
			const { data: merchant, error: merchantError } = await supabase
				.from('merchant')
				.insert(insert)
				.select('id')
				.single()
			if (merchantError) throw merchantError
			const { error: updateError } = await supabase
				.from('merchant')
				.update({ slug: merchant.id })
				.eq('id', merchant.id)
			if (updateError) throw updateError
			await supabase.from('employees').insert({
				merchant_id: merchant.id,
				first_name: 'Example',
				last_name: 'Employee',
				services: [],
				active: true
			})
			if (isPostHogConfigured) {
				posthog.capture('merchant_account_created', { currency })
				logPostHogWorkflow('merchant account created', {
					event: 'merchant_account_created',
					currency,
				})
			}
			window.location.assign('/dashboard')
		} catch (err) {
			setErrorMessage(err.message || "Failed to create merchant. Please try again.")
		} finally {
			setCreating(false)
		}
	}

	// Passcode screen
	// if (!passcodeVerified) {
	// 	return <PasscodeScreen onVerified={() => setPasscodeVerified(true)} />
	// }

	// Account lookup failed: show a retry screen, never the sign-up form.
	if (session && setupCheckError) {
		return (
			<Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, p: 2 }}>
				<Box sx={{ textAlign: 'center', maxWidth: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					<img src={namiLogo} alt="Nami" style={{ width: 48, height: 48 }} />
					<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 18, color: '#2D2D36' }}>
						Connection problem
					</Typography>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#8B8D97' }}>
						{setupCheckError}
					</Typography>
					<Button
						variant="contained"
						onClick={() => setSetupCheckAttempt((n) => n + 1)}
						sx={{
							mt: 1, px: 4, py: 1.25, borderRadius: '16px', bgcolor: PRIMARY,
							fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 12,
							textTransform: 'none', color: '#F5F5F5', '&:hover': { bgcolor: '#B84CFF' }
						}}
					>
						Try again
					</Button>
					<Box
						component="button"
						type="button"
						onClick={async () => { await supabase.auth.signOut(); window.location.assign('/login') }}
						sx={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 12, color: PRIMARY }}
					>
						Sign out
					</Box>
				</Box>
			</Box>
		)
	}

	// Loading state
	if (checkingSession) {
		return (
			<Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, p: 2 }}>
				<Box sx={{ textAlign: 'center' }}>
					<img src={namiLogo} alt="Nami" style={{ width: 48, height: 48, marginBottom: 16 }} />
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: '#8B8D97' }}>Loading...</Typography>
				</Box>
			</Box>
		)
	}

	// Auth / Verify step (Figma: single centered card, desktop + mobile)
	if (!session) {
		const formCard = (
			<Card sx={{
				width: '100%',
				maxWidth: isMobile ? '100%' : '433px',
				borderRadius: '12px',
				bgcolor: '#FFFFFF',
				px: isMobile ? 0 : '50px',
				py: isMobile ? 0 : '44px',
				boxShadow: 'none',
				overflow: 'hidden',
				...(isMobile && { flex: 1, minHeight: 0 })
			}}>
				<CardContent sx={{
					p: isMobile ? '80px 24px calc(50px + env(safe-area-inset-bottom, 0px))' : '0px !important',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: isMobile ? undefined : 7.5,
					height: isMobile ? '100%' : undefined,
					overflowY: isMobile ? 'auto' : undefined,
					boxSizing: 'border-box'
				}}>
					{/* Heading: logo + title + subtitle (Figma: gap 20px, logo 70x70) */}
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 2.5 : 3.75, pt: isMobile ? '80px' : 0 }}>
						<img src={namiLogo} alt="Nami" style={{ width: isMobile ? 70 : 60, height: isMobile ? 70 : 60 }} />
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
							<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: 1.5, color: '#000000', textAlign: 'center' }}>
								Get Started with Nami
							</Typography>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, lineHeight: 1.21, color: '#8B8D97', textAlign: 'center' }}>
								Create your free account
							</Typography>
						</Box>
					</Box>

					{/* Form: 80px gap from heading, then form content, spacer pushes Send OTP to bottom (mobile) */}
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: isMobile ? 4.5 : 4,
						width: '100%',
						alignSelf: 'stretch',
						...(isMobile && { mt: '80px', flex: 1, minHeight: 0 })
					}}>
						{step === 'auth' && (
							<Typography sx={{
								fontFamily: isMobile ? 'Inter, sans-serif' : 'Poppins, sans-serif',
								fontWeight: 600,
								fontSize: isMobile ? 18 : 20,
								lineHeight: 1.21,
								color: '#2D2D36'
							}}>
								E-mail Verification
							</Typography>
						)}

						{step === 'auth' && (
							<form
								onSubmit={requestCode}
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: isMobile ? 36 : 32,
									width: '100%',
									...(isMobile && { flex: 1, minHeight: 0 })
								}}
							>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Typography sx={{
										fontFamily: isMobile ? 'Inter, sans-serif' : 'Poppins, sans-serif',
										fontWeight: 600,
										fontSize: isMobile ? 14 : 12,
										lineHeight: 1.21,
										color: "#C85AFF"
									}}>
										E-mail
									</Typography>
									<TextField
										id="email"
										type="email"
										placeholder="Enter email address"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
										fullWidth
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<MailOutlineIcon sx={{ fontSize: 24, color: '#C85AFF', opacity: 0.7 }} />
												</InputAdornment>
											)
										}}
										sx={{
											'& .MuiOutlinedInput-root': {
												bgcolor: EMAIL_BG,
												borderRadius: '12px',
												'& fieldset': { border: 'none' },
												'&.Mui-focused': { bgcolor: EMAIL_BG }
											}
										}}
									/>
								</Box>

								<Button
									type="button"
									variant="outlined"
									fullWidth
									onClick={signInWithGoogle}
									startIcon={<Box component="img" src={googleIcon} alt="" sx={{ width: 24, height: 24 }} />}
									sx={{
										py: 1.5,
										borderRadius: '12px',
										borderColor: BORDER_GOOGLE,
										bgcolor: 'rgba(230, 102, 26, 0.02)',
										color: '#2B2829',
										fontFamily: 'Manrope, sans-serif',
										fontWeight: 700,
										fontSize: 14,
										lineHeight: 1.37,
										textTransform: 'none',
										'&:hover': { borderColor: BORDER_GOOGLE, bgcolor: 'rgba(230, 102, 26, 0.04)' }
									}}
								>
									Sign in with Google
								</Button>

								{/* Spacer: pushes Send OTP to bottom on mobile */}
								{isMobile && <Box sx={{ flex: 1, minHeight: 0 }} />}

								<Button
									type="submit"
									variant="contained"
									fullWidth
									sx={{
										py: isMobile ? 2 : 1.25,
										borderRadius: isMobile ? '20px' : '16px',
										bgcolor: PRIMARY,
										fontFamily: isMobile ? 'Inter, sans-serif' : 'Poppins, sans-serif',
										fontWeight: 700,
										fontSize: isMobile ? 14 : 12,
										lineHeight: 1.21,
										textTransform: 'none',
										color: '#F5F5F5',
										'&:hover': { bgcolor: '#B84CFF' }
									}}
								>
									Send OTP
								</Button>
							</form>
						)}

						{step === 'verify' && (
							<form
								onSubmit={verifyCode}
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: isMobile ? 36 : 32,
									width: '100%',
									...(isMobile && { flex: 1, minHeight: 0 })
								}}
							>
								{/* Verify Email Address / Enter OTP (Figma) */}
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
									<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 20, lineHeight: 1.5, color: '#2D2D36' }}>
										Verify Email Address
									</Typography>
									<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: 14, lineHeight: 1.5, color: '#767676' }}>
										Enter OTP
									</Typography>
								</Box>

								{/* 6-digit OTP boxes (Figma: 49x49, gap ~20px; mobile: smaller to fit single row) */}
								<Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'nowrap', width: '100%' }}>
									{[0, 1, 2, 3, 4, 5].map((i) => (
										<Box
											key={i}
											component="input"
											type="text"
											inputMode="numeric"
											maxLength={1}
											value={code[i] || ''}
											onChange={(e) => {
												const v = e.target.value.replace(/\D/g, '').slice(-1)
												const newCode = (code.slice(0, i) + v + code.slice(i + 1)).slice(0, 6)
												setCode(newCode)
												if (v && i < 5) setTimeout(() => otpRefs.current[i + 1]?.focus(), 0)
											}}
											onKeyDown={(e) => {
												if (e.key === 'Backspace' && !code[i] && i > 0) {
													setTimeout(() => otpRefs.current[i - 1]?.focus(), 0)
												}
											}}
											onPaste={(e) => {
												e.preventDefault()
												const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
												if (pasted) {
													setCode(pasted)
													setTimeout(() => otpRefs.current[Math.min(pasted.length, 5)]?.focus(), 0)
												}
											}}
											ref={(el) => { otpRefs.current[i] = el }}
											sx={{
												width: isMobile ? 44 : 49,
												height: isMobile ? 44 : 49,
												padding: isMobile ? '5px' : '6.5px',
												borderRadius: isMobile ? '6px' : '6.5px',
												border: '1.6px solid',
												borderColor: code[i] ? PRIMARY : '#CFDBEC',
												bgcolor: code[i] ? 'transparent' : 'rgba(230, 102, 26, 0.02)',
												fontFamily: 'Manrope, sans-serif',
												fontWeight: 700,
												fontSize: isMobile ? 18 : 22,
												lineHeight: 1.37,
												letterSpacing: '0.03em',
												color: '#1C2035',
												textAlign: 'center',
												'&:focus': { outline: 'none', borderColor: PRIMARY }
											}}
										/>
									))}
								</Box>

								{/* Enter code sent to your email | Use a different email (Figma: row, space-between on desktop; column on mobile) */}
								<Box sx={{
									display: 'flex',
									flexDirection: isMobile ? 'column' : 'row',
									justifyContent: 'space-between',
									alignItems: isMobile ? 'flex-start' : 'center',
									gap: isMobile ? 0.5 : 4,
									width: '100%',
									maxWidth: 334
								}}>
									<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 12, lineHeight: 1.5, color: '#767676' }}>
										Enter code sent to your email
									</Typography>
									<Box
										component="button"
										type="button"
										onClick={() => { setStep('auth'); setCode('') }}
										sx={{
											background: 'none',
											border: 'none',
											padding: 0,
											cursor: 'pointer',
											fontFamily: 'Poppins, sans-serif',
											fontWeight: 600,
											fontSize: 12,
											lineHeight: 1.5,
											color: PRIMARY,
											textAlign: 'left'
										}}
									>
										Use a different email
									</Box>
								</Box>

								{/* Spacer: pushes Verify to bottom on mobile */}
								{isMobile && <Box sx={{ flex: 1, minHeight: 0 }} />}

								<Button
									type="submit"
									variant="contained"
									fullWidth
									disabled={code.length !== 6}
									sx={{
										py: isMobile ? 2 : 1.25,
										borderRadius: isMobile ? '20px' : '16px',
										bgcolor: PRIMARY,
										fontFamily: 'Poppins, sans-serif',
										fontWeight: 600,
										fontSize: 14,
										textTransform: 'none',
										color: '#F5F5F5',
										'&:hover': { bgcolor: '#B84CFF' },
										'&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#A0AEC0' }
									}}
								>
									Verify
								</Button>

								{/* Didn't receive code? Resend code (Figma) */}
								<Box sx={{ display: 'flex', justifyContent: 'center' }}>
									<Box
										component="button"
										type="button"
										onClick={() => requestCode()}
										sx={{
											background: 'none',
											border: 'none',
											padding: 0,
											cursor: 'pointer',
											fontFamily: 'Poppins, sans-serif',
											fontWeight: 600,
											fontSize: 12,
											lineHeight: 1.5,
											color: '#767676'
										}}
									>
										Didn't receive code? Resend code
									</Box>
								</Box>
							</form>
						)}
						{errorMessage && (
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#E53E3E' }}>{errorMessage}</Typography>
						)}
					</Box>
				</CardContent>
			</Card>
		)

		return (
			<Box sx={{
				minHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				background: isMobile ? '#FFFFFF' : BG,
				pt: 0,
				pb: isMobile ? 0 : 6,
				px: 0
			}}>
				{/* Top header - desktop */}
				{!isMobile && (
					<Box sx={{ width: '100%', height: 76, display: 'flex', alignItems: 'center', px: 5, bgcolor: '#FFFFFF' }}>
						<img src={namiLogo} alt="Nami" style={{ width: 52, height: 52 }} />
					</Box>
				)}
				<Box sx={{
					flex: 1,
					minHeight: 0,
					display: 'flex',
					alignItems: isMobile ? 'stretch' : 'center',
					justifyContent: 'center',
					width: '100%',
					mt: isMobile ? 0 : 4
				}}>
					{formCard}
				</Box>
			</Box>
		)
	}

	// Merchant setup step
	if (step === 'merchant') {
		// Shared field styles
		const fieldLabelSx = { fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 12, lineHeight: 1.5, color: PRIMARY }
		const inputSx = {
			'& .MuiOutlinedInput-root': {
				bgcolor: EMAIL_BG,
				borderRadius: '12px',
				height: 38,
				'& fieldset': { border: 'none' },
				'&.Mui-focused fieldset': { border: 'none' }
			},
			'& .MuiInputBase-input': {
				fontFamily: 'Poppins, sans-serif',
				fontWeight: 600,
				fontSize: 12,
				color: '#767676',
				py: 0
			},
			'& .MuiInputBase-input::placeholder': { opacity: 0.7 }
		}
		const iconSx = { fontSize: 22, color: '#C85AFF', opacity: 0.7 }

		return (
			<Box sx={{
				minHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				background: isMobile ? '#FFFFFF' : BG,
				pt: 0,
				pb: isMobile ? 0 : 6,
				px: 2
			}}>
				{!isMobile && (
					<Box sx={{ width: '100%', height: 76, display: 'flex', alignItems: 'center', px: 5, bgcolor: '#FFFFFF' }}>
						<img src={namiLogo} alt="Nami" style={{ width: 52, height: 52 }} />
					</Box>
				)}
				<Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', width: '100%', mt: isMobile ? 0 : 4 }}>
					<Card sx={{
						width: '100%',
						maxWidth: isMobile ? '100%' : 480,
						borderRadius: '12px',
						bgcolor: '#FFFFFF',
						boxShadow: 'none',
						overflow: 'hidden',
						...(isMobile && { flex: 1, minHeight: 0 })
					}}>
						<CardContent sx={{
							p: isMobile ? '80px 24px calc(50px + env(safe-area-inset-bottom, 0px))' : '44px 34px',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: isMobile ? 10 : 7.5,
							height: isMobile ? '100%' : undefined,
							overflowY: isMobile ? 'auto' : undefined,
							boxSizing: 'border-box'
						}}>
							{/* Heading */}
							<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 2.5 : 3.75 }}>
								{isMobile && <img src={namiLogo} alt="Nami" style={{ width: 70, height: 70 }} />}
								{!isMobile && <img src={namiLogo} alt="Nami" style={{ width: 60, height: 60 }} />}
								<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
									<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: 1.5, color: '#000000', textAlign: 'center' }}>
										Get Started with Nami
									</Typography>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, lineHeight: 1.21, color: '#8B8D97', textAlign: 'center' }}>
										Create your free account
									</Typography>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: 1.4, color: '#8B8D97', textAlign: 'center', mt: 1 }}>
										Signed in as <Box component="span" sx={{ fontWeight: 600, color: '#2D2D36' }}>{session?.user?.email || 'unknown email'}</Box>
										<br />
										Already have a business on Nami with a different email?{' '}
										<Box
											component="button"
											type="button"
											onClick={useDifferentAccount}
											sx={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: PRIMARY }}
										>
											Sign out and use a different account
										</Box>
									</Typography>
								</Box>
							</Box>

							{/* Form */}
							<Box
								component="form"
								onSubmit={createMerchant}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 3,
									width: '100%',
									alignSelf: 'stretch',
									...(isMobile && { mt: '80px', flex: 1, minHeight: 0 })
								}}
							>
								{/* Business name */}
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Typography sx={fieldLabelSx}>Business name *</Typography>
									<TextField
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="Enter business name"
										required
										fullWidth
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<StorefrontOutlinedIcon sx={iconSx} />
												</InputAdornment>
											)
										}}
										sx={inputSx}
									/>
								</Box>

								{/* Timezone */}
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Typography sx={fieldLabelSx}>Select Timezone *</Typography>
									<TextField
										value={timezone}
										onChange={(e) => setTimezone(e.target.value)}
										select
										required
										fullWidth
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<AccessTimeOutlinedIcon sx={iconSx} />
												</InputAdornment>
											)
										}}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{
											...inputSx,
											'& .MuiSelect-select': {
												fontFamily: 'Poppins, sans-serif',
												fontWeight: 600,
												fontSize: 12,
												color: '#2D2D36',
												py: 0
											},
											'& .MuiSvgIcon-root.MuiSelect-icon': { color: '#767676', opacity: 0.7, fontSize: 18 }
										}}
									>
										{TIMEZONES.map((tz) => {
											const offset = tz.rawOffsetInMinutes
											const hours = Math.floor(Math.abs(offset) / 60)
											const mins = Math.abs(offset) % 60
											const sign = offset >= 0 ? '+' : '-'
											const gmtLabel = `GMT${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
											return <MenuItem key={tz.name} value={tz.name} sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 12 }}>{tz.name} ({gmtLabel})</MenuItem>
										})}
									</TextField>
								</Box>

								{/* Currency */}
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Typography sx={fieldLabelSx}>Currency *</Typography>
									<TextField
										value={currency}
										onChange={(e) => setCurrency(e.target.value)}
										select
										required
										fullWidth
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{
											...inputSx,
											'& .MuiSelect-select': {
												fontFamily: 'Poppins, sans-serif',
												fontWeight: 600,
												fontSize: 12,
												color: '#2D2D36',
												py: 0
											},
											'& .MuiSvgIcon-root.MuiSelect-icon': { color: '#767676', opacity: 0.7, fontSize: 18 }
										}}
									>
										{SUPPORTED_CURRENCIES.map((c) => (
											<MenuItem key={c.code} value={c.code} sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 12 }}>{c.label}</MenuItem>
										))}
									</TextField>
								</Box>

								{/* Phone */}
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									<Typography sx={fieldLabelSx}>Phone number</Typography>
									<TextField
										value={(() => {
											const d = phone.replace(/\D/g, '').slice(0, 10)
											if (!d) return ''
											if (d.length <= 3) return `(${d}`
											if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
											return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
										})()}
										onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
										placeholder="(xxx) xxx-xxxx"
										inputMode="tel"
										fullWidth
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<PhoneOutlinedIcon sx={iconSx} />
												</InputAdornment>
											)
										}}
										sx={inputSx}
									/>
									<Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 300, fontSize: 12, lineHeight: 1.5, color: 'rgba(45,45,54,0.7)', mt: 0.5 }}>
										This can be added later on your account settings
									</Typography>
								</Box>

								{/* Spacer pushes button to bottom on mobile */}
								{isMobile && <Box sx={{ flex: 1, minHeight: 0 }} />}

								<Button
									type="submit"
									variant="contained"
									fullWidth
									disabled={!name.trim() || creating}
									sx={{
										py: isMobile ? 2 : 1.25,
										borderRadius: isMobile ? '20px' : '16px',
										bgcolor: PRIMARY,
										fontFamily: 'Poppins, sans-serif',
										fontWeight: 600,
										fontSize: 12,
										textTransform: 'none',
										color: '#F5F5F5',
										'&:hover': { bgcolor: '#B84CFF' },
										'&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#A0AEC0' }
									}}
								>
									{creating ? 'Creating...' : 'Create account'}
								</Button>

								{errorMessage && (
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#E53E3E' }}>
										{errorMessage}
									</Typography>
								)}
							</Box>
						</CardContent>
					</Card>
				</Box>
			</Box>
		)
	}

	return null
}

export default MerchantSignup
